import { ROLE, SCHOLARSHIPS_REVIEWER } from '../constants/general';

export const getRowFromListById = (list, id) => {
  let searchList = [];
  searchList = list;
  const item = searchList.filter((s) => {
    return s.id === id;
  });
  return item ? item[0] : null;
};

export const getInitial = (name) => {
  if (!name) return '';
  return String(name).trim().charAt(0).toUpperCase();
};

export const convertSiteCode = (code) => {
  switch (code) {
    case 'HC':
      return 'Home Campus';
    case 'CFLA':
      return 'Central Florida';
    case 'GI':
      return 'Grand Island';
    case 'IA':
      return 'Iowa';
    case 'LA':
      return 'Louisiana';
    case 'NV':
      return 'Nevada';
    case 'NE':
      return 'New England';
    case 'NFLA':
      return 'North Florida';
    case 'SFLA':
      return 'South Florida';
    case 'WDC':
      return 'Washington DC';
    default:
      return code;
  }
};

const VALID_FILTERS_FIELDS_FOR_DIGITISATION_ADMIN = ['file_name', 'uploaded_by'];

const validateFilters = (filters) => {
  const invalidFilters = Object.keys(filters).filter((key) => !VALID_FILTERS_FIELDS_FOR_DIGITISATION_ADMIN.includes(key));
  if (invalidFilters.length > 0) {
    throw new Error(`Invalid filters: ${invalidFilters.join(', ')}`);
  }
};

export const prepareSearchParam = (filters) => {
  if (filters && Object.keys(filters).length > 0) {
    validateFilters(filters);
    return Object.entries(filters)
      .filter(([key, value]) => value !== '' && key === 'file_name')
      .map(([key, value]) => `search=${value}`)
      .map((param) => param.replace(/ /g, '%20'))
      .join('&')
      .replace(/^/, '&');
  }
  return '';
};

export const prepareFilters = (filters) => {
  if (filters && Object.keys(filters).length > 0) {
    validateFilters(filters);
    return Object.entries(filters)
      .map(([key, value]) => `Filters[${key}]=${value}`)
      .join('&')
      .replace(/^/, '&');
  }
  return '';
};

/**
 * Remove leading number(s) and period from a label string, e.g. "1. Foo" => "Foo"
 * @param {string} label
 * @returns {string}
 */
export function stripLeadingNumber(label) {
  return label.replace(/^\d+\.\s*/, '');
}

/**
 * Count the number of checklist items in a list, optionally filtering by type and version.
 * By default, counts only items that are not type: 'instruction'.
 * @param {Array} items - The checklist items array
 * @param {Object} [options] - Optional filter options
 * @param {boolean} [options.includeInstructions=false] - If true, include items with type: 'instruction'
 * @param {number} [version] - Optional version to filter items by minVersion/maxVersion
 * @returns {number}
 */
export function countChecklistItems(items, options = {}, version) {
  return getChecklistItems(items, (options = {}), version).length;
}

export function getChecklistItems(items, options = {}, version) {
  const { includeInstructions = false, includeGroupMembers = false } = options;
  if (!Array.isArray(items)) return [];
  return items.filter((item) => {
    // Version filter: if a version is provided, the item must match minVersion/maxVersion
    const versionMatch =
      typeof version !== 'number' ||
      ((item.minVersion === undefined || item.minVersion <= version) && (item.maxVersion === undefined || item.maxVersion >= version));
    // Instruction filter
    const instructionMatch = includeInstructions || item.type !== 'instruction';
    // group-member filter
    const groupMemberMatch = includeGroupMembers || item.type !== 'group-member';
    return versionMatch && instructionMatch && groupMemberMatch;
  });
}

/**
 * Given an array of arrays (sections), returns an array of start indices for each section.
 * @param {Array[]} sections - Array of arrays, each representing a checklist section's items
 * @param {number} initialIndex - The starting index (default 1)
 * @returns {number[]} Array of start indices for each section
 */
export function getStartIndices(sections, initialIndex = 1) {
  const indices = [];
  let current = initialIndex;
  for (const items of sections) {
    indices.push(current);
    current += items.length;
  }
  return indices;
}

/**
 * Sums the 'points' property of all items in an array.
 * @param {Array} items - The checklist items array
 * @returns {number}
 */
export function sumPoints(items) {
  if (!Array.isArray(items)) return 0;
  return items.reduce((sum, item) => sum + (item.points || 0), 0);
}

export const lookupSpecialistName = (assigned_to, specialistList) => {
  if (!assigned_to || assigned_to === 0 || !Array.isArray(specialistList) || specialistList.length === 0) return '';
  const specialist = specialistList.find((s) => s.id === assigned_to);
  if (specialist) {
    return specialist.user.first_name + ' ' + specialist.user.last_name;
  } else {
    return '';
  }
};

export const lookupSpecialistNameWithInitial = (assigned_to, specialistList) => {
  if (!assigned_to || assigned_to === 0 || !Array.isArray(specialistList) || specialistList.length === 0) return '';
  const specialist = specialistList.find((s) => s.id === assigned_to);
  if (specialist) {
    return getInitial(specialist.user.first_name) + '. ' + specialist.user.last_name;
  } else {
    return '';
  }
};

export const isEmailValid = (email) => {
  if (!email) return false;
  if (!email.includes('@')) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const standarizeEmail = (email) => {
  if (email && isEmailValid(email)) {
    const emailParts = email.split('@');
    const domainParts = emailParts[1].split('.');
    return emailParts[0].toLowerCase() + '@' + domainParts[0].toLowerCase() + '.' + domainParts[1].toLowerCase();
  } else {
    return '';
  }
};

export const displayStatus = (status) => {
  if (!status) return '';
  const lowerStatus = status.toLowerCase();
  if (lowerStatus === 'qualified' || lowerStatus === 'qualifiedinitial' || lowerStatus === 'qualifiedprogram') {
    return 'UNDER REVIEW';
  }
  if (lowerStatus === 'approved') {
    return 'PROCESSED';
  }
  if (lowerStatus === 'awarded') {
    return 'PROCESSED';
  }
  if (lowerStatus === 'approvedalternate') {
    return 'PROCESSED';
  }
  if (lowerStatus === 'denied') {
    return 'PROCESSED';
  }
  if (lowerStatus === 'new') {
    return 'NEW';
  }
  if (lowerStatus === 'inprogress') {
    return 'IN PROGRESS';
  }
  if (lowerStatus === 'interviewscheduled') {
    return 'INTERVIEW';
  }
  if (lowerStatus === 'reopened') {
    return 'RE-OPENED';
  }
  return status.toUpperCase();
};

export const formatCurrencyAmount = (value, locale = 'en-US', currency = 'USD') => {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  const parsed = Number(value);
  if (!Number.isNaN(parsed) && Number.isFinite(parsed)) {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(parsed);
  }

  return String(value);
};

export const formatPhoneNumber = (str) => {
  const cleaned = ('' + str).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3];
  }

  return null;
};

export const isStudent = (roles) => roles && roles.includes('STUDENT');
export const isAlumni = (roles) => roles && roles.includes('ALUMNI');
export const isFHT = (roles) => roles && roles.includes('FHT');
export const isAftercareSpecialist = (roles) => roles && roles.includes('AFTERCARE_SPECIALIST');
export const isAdmin = (roles) => roles && roles?.some((role) => [ROLE.ADMIN, ROLE.AFTERCARE_SPECIALIST].includes(role));
export const isAdminOrFHT = (roles) => roles && roles?.some((role) => [ROLE.ADMIN, ROLE.AFTERCARE_SPECIALIST, ROLE.FHT].includes(role));
export const isStudentOrAlumni = (roles) => roles && roles?.some((role) => ['STUDENT', 'ALUMNI'].includes(role));
export const isScholarshipCoordinator = (roles, positionTitle) =>
  roles && roles?.some((role) => [ROLE.AFTERCARE_SPECIALIST].includes(role)) && SCHOLARSHIPS_REVIEWER.includes(positionTitle);
